// Contexto global de "qual instituição está selecionada" — um usuário pode ter
// acesso a várias instituições (ver perfil "master"/coordenador em AuthContext) e
// escolhe uma por vez para trabalhar. O id escolhido é enviado em toda chamada de
// API via header x-institution-id (ver App.js/axios interceptor e server.js).
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '../api/config';

const InstitutionContext = createContext();

// InstitutionProvider é montado DENTRO de <AuthProvider> (ver App.js), então pode
// consumir useAuth() com segurança para saber se há uma sessão válida.
export const InstitutionProvider = ({ children }) => {
  const { token, loadingAuth } = useAuth();
  const [institutionId, setInstitutionId] = useState(() => {
    return localStorage.getItem('selectedInstitutionId') || null;
  });
  // Restaura o nome salvo imediatamente — evita flash de nome errado enquanto o fetch carrega
  const [institutionName, setInstitutionName] = useState(() => {
    const savedId = localStorage.getItem('selectedInstitutionId');
    const savedName = localStorage.getItem('selectedInstitutionName');
    // Só usa o nome salvo se o ID ainda for o mesmo
    return savedId && savedName ? savedName : '';
  });

  // Espera a validação de sessão do AuthContext terminar (loadingAuth) antes de
  // buscar dados de instituição. Sem isso, um token salvo mas já inválido (ex.:
  // expirado, ou invalidado por uma troca do AUTH_SECRET no servidor) ainda é
  // "verdadeiro" no primeiro render — o fetch abaixo dispararia com esse token
  // ruim e tomaria 401 antes mesmo do AuthContext descobrir que a sessão caiu e
  // fazer logout(). Esperar loadingAuth=false garante que `token` já reflete o
  // resultado definitivo (válido ou null) checado contra o backend.
  useEffect(() => {
    if (loadingAuth || !token) return;

    if (institutionId) {
      fetch(`${API_URL}/api/instituicao`, {
        headers: {
          'x-institution-id': institutionId,
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          // 403 = o usuário não está mais vinculado a essa instituição
          // (ex.: um master removeu o vínculo agora, sem esperar o token
          // expirar) — limpa a seleção salva pra cair no seletor de
          // instituição em vez de ficar preso numa tela com tudo
          // quebrado (toda chamada de API seguinte tomaria o mesmo 403).
          if (res.status === 403) {
            setInstitutionId(null);
            setInstitutionName('');
            localStorage.removeItem('selectedInstitutionId');
            localStorage.removeItem('selectedInstitutionName');
            return null;
          }
          return res.json();
        })
        .then((body) => {
          const data = body?.data ?? body;
          if (data && data.nome) {
            setInstitutionName(data.nome);
            // Salva o nome junto com o ID para restaurar instantaneamente na próxima vez
            localStorage.setItem('selectedInstitutionName', data.nome);
          }
        })
        .catch((err) => console.error('Erro ao buscar dados da instituição:', err));
    } else {
      setInstitutionName('');
      localStorage.removeItem('selectedInstitutionName');
    }
  }, [institutionId, token, loadingAuth]);

  // Permissões (telas/recursos) por perfil agora são POR INSTITUIÇÃO — cada
  // instituição configura o seu próprio conjunto (ver
  // backend/migrate-permissoes-por-instituicao.js), então não dá mais pra
  // confiar no que veio cravado no token do login (ver usePermissao.js,
  // PerfilRoute em App.js e o menu em Layout.jsx, que agora consomem daqui).
  // Refaz a busca toda vez que a instituição ativa muda (troca de unidade ou
  // seleção inicial), sem precisar de F5 nem novo login.
  const [permissoes, setPermissoes] = useState({ telas_permitidas: [], recursos_permitidos: {} });
  const [carregandoPermissoes, setCarregandoPermissoes] = useState(true);

  useEffect(() => {
    if (loadingAuth || !token || !institutionId) {
      setCarregandoPermissoes(!!token);
      return;
    }
    let cancelado = false;
    setCarregandoPermissoes(true);
    fetch(`${API_URL}/api/auth/minhas-permissoes`, {
      headers: {
        'x-institution-id': institutionId,
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const payload = data?.data ?? data;
        if (!cancelado && payload) {
          setPermissoes({
            telas_permitidas: payload.telas_permitidas || [],
            recursos_permitidos: payload.recursos_permitidos || {},
          });
        }
      })
      .catch((err) => console.error('Erro ao buscar permissões da instituição ativa:', err))
      .finally(() => {
        if (!cancelado) setCarregandoPermissoes(false);
      });
    return () => {
      cancelado = true;
    };
  }, [institutionId, token, loadingAuth]);

  // Quando a sessão termina de carregar e não há token (logout explícito, sessão
  // expirada ou invalidada), limpa também a instituição selecionada — evita ficar
  // com uma seleção "órfã" salva no navegador que dispara chamadas de API sem
  // token válido na próxima visita.
  useEffect(() => {
    if (loadingAuth) return;
    if (!token && institutionId) {
      setInstitutionId(null);
      setInstitutionName('');
      localStorage.removeItem('selectedInstitutionId');
      localStorage.removeItem('selectedInstitutionName');
    }
  }, [token, institutionId, loadingAuth]);

  // Troca a instituição ativa (chamado pela tela de seleção de instituição).
  // Passar id=null limpa a seleção (usado no logout).
  const selectInstitution = (id, nome) => {
    setInstitutionId(id);
    if (id) {
      localStorage.setItem('selectedInstitutionId', id);
      if (nome) {
        setInstitutionName(nome);
        localStorage.setItem('selectedInstitutionName', nome);
      }
    } else {
      localStorage.removeItem('selectedInstitutionId');
      localStorage.removeItem('selectedInstitutionName');
    }
  };

  const saveInstitutionName = (nome) => {
    setInstitutionName(nome);
    if (nome) localStorage.setItem('selectedInstitutionName', nome);
  };

  return (
    <InstitutionContext.Provider
      value={{
        institutionId,
        institutionName,
        selectInstitution,
        setInstitutionName: saveInstitutionName,
        setInstitutionId,
        permissoes,
        carregandoPermissoes,
      }}
    >
      {children}
    </InstitutionContext.Provider>
  );
};

export const useInstitution = () => {
  return useContext(InstitutionContext);
};
